import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledInputSelect } from '@components/controlled-input-select';
import { FIELD_QUANTITY, REQUIRED_TEXT_ERROR } from '@components/dynamic-input-fields/src/utils';
import { IconPerson, IconUsers } from '@components/icons';
import { InputCounter } from '@components/input-counter';
import { LabelInfo } from '@components/label/src/label-info';
import { Loader } from '@components/loader';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { ModalStickyHeader } from '@components/modals/modal-sticky-header';
import { MULTIPLE_INVITE, SINGLE_INVITE } from '@components/process-invite-guest/src/process-invite-guest.type';
import { SORT_ASC } from '@constants/global';
import { CompanyClubContext } from '@contexts/companyClub/companyClub-context';
import { ActiveChannel } from '@domain/channel';
import { EventListItem } from '@domain/event';
import { Invitation } from '@domain/invitation';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useGetEventList } from '@hooks/events/use-get-event-list';
import { useGetEventInvitations } from '@hooks/invitation/use-get-event-invitations';
import { EventView } from '@hooks/use-event-navigation/use-event-navigation';
import { Divider } from '@tremor/react';
import { formatDateName } from '@utils/format-date/format-date';
import { getDefaultOfferNameTranslation } from '@utils/get-default-offer-name-translation/get-default-offer-name-translation';
import { getTranslatedPropertyBasedOnOrder } from '@utils/get-translated-property-based-on-order/get-translated-property-based-on-order';
import moment from 'moment';

import { ControlledMultiOfferSelector } from '../multi-offer/controlled-multi-offer-selector';
import { createMultiSelectorOption } from '../utils/create-multi-selector-option';

import { InviteGuestSelectTypeProps } from './invite-guest.types';
import { MultiOfferSelectorOptions } from './select-type-types';

export const SelectType = ({ onContinue, offer }: InviteGuestSelectTypeProps) => {
  const {
    t,
    i18n: { language: locale },
  } = useTranslation();
  const {
    register,
    formState: { isValid },
    watch,
    unregister,
    setValue,
  } = useFormContext();
  const {
    companyState: { id: companyId },
    clubState: {
      id: clubId,
      city: { timezone },
    },
  } = useContext(CompanyClubContext);
  const [channelList, setChannelList] = useState<ActiveChannel[]>([]);

  const eventId = watch('eventId');
  const channelId = watch('channelId');
  const invitationId = watch('invitationId');
  const invitationType = watch('type');
  const quantity = watch(FIELD_QUANTITY);

  const todayNow = useMemo(() => moment().tz(timezone).unix(), [timezone]);

  const {
    data: eventList,
    isInitialLoading: isEventListLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetEventList({
    companyId,
    clubId,
    view: 'all' as EventView,
    params: {
      startTime: todayNow,
      sort: SORT_ASC,
      orderBy: 'date',
      limit: 50,
    },
  });
  const eventListFiltered = eventList?.pages?.map((page) => page.filter((event) => !event.settings.isHidden));
  const { data: invitationList } = useGetEventInvitations({
    eventId: eventId ?? '',
  });

  const { data: eventChannels } = useGetChannels({ eventId, enabled: !!eventId && !!invitationId });

  const handleEventSelectorChange = (e: React.FormEvent<HTMLSelectElement>) => {
    if (invitationId && !e.currentTarget.value)
      setValue('invitationId', '', { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  };

  useEffect(() => {
    if (eventId && !invitationId && invitationList?.length === 1) {
      setValue('invitationId', invitationList[0].id, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invitationId, eventId, invitationList]);

  useEffect(() => {
    if (eventChannels && offer) {
      const channelsFormatted = eventChannels
        .filter((channel) => channel.isEnabled)
        .map((channel) => ({
          ...channel.channel,
          maxQuantity: null,
        }));

      setChannelList(channelsFormatted);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventChannels, offer]);

  if (hasNextPage) fetchNextPage();

  const toggleInviteType = (type: typeof SINGLE_INVITE | typeof MULTIPLE_INVITE) => {
    if (invitationType === type) return;
    setValue('type', type, {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    });
  };

  const typeOptions: MultiOfferSelectorOptions[] = useMemo(() => {
    return [
      createMultiSelectorOption({
        id: SINGLE_INVITE,
        name: t('invitations:singleInviteOption'),
        subtitle: t('invitations:singleInviteSubtitle'),
        icon: IconPerson,
        value: invitationType,
        toggler: toggleInviteType,
      }),
      createMultiSelectorOption({
        id: MULTIPLE_INVITE,
        name: t('invitations:multiInviteOption'),
        subtitle: t('invitations:multiInviteSubtitle'),
        icon: IconUsers,
        value: invitationType,
        toggler: toggleInviteType,
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, invitationType]);

  useEffect(() => {
    if (invitationType === MULTIPLE_INVITE) {
      unregister(FIELD_QUANTITY);
    } else {
      register(FIELD_QUANTITY);
      setValue(FIELD_QUANTITY, 1, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
    }
  }, [unregister, register, invitationType, setValue]);

  const invitationItemsPerTransaction = invitationList?.find(
    (i: Invitation) => i.id === invitationId
  )?.itemsPerTransaction;

  const invitationName = (invitation: Invitation) =>
    getTranslatedPropertyBasedOnOrder({
      translations: invitation.name,
      preferredLanguage: locale,
    }) ?? t(getDefaultOfferNameTranslation('invitation'));

  return (
    <>
      <ModalStickyHeader
        title={
          <span className='flex flex-grow items-center gap-3 text-left text-headline-m text-primary'>
            <IconUsers className='w-6 h-6' />
            <LabelInfo isBold text={t('invitations:invitationsEmptyText')}>
              {t('invitations:sendInvitationsTitle')}
            </LabelInfo>
          </span>
        }
      />
      <div className='flex flex-col gap-4 px-4 pb-6 sm:px-20 sm:pb-12'>
        {isEventListLoading && <Loader />}
        {!!eventListFiltered && (
          <section>
            <ControlledInputSelect
              fieldRegisterName='eventId'
              options={eventListFiltered.flat().map((e: EventListItem) => ({
                ...e,
                name: formatDateName(e.name, e.startingTime, timezone),
              }))}
              label={t('invitations:selectEventId')}
              rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
              onChange={handleEventSelectorChange}
            />
          </section>
        )}
        {!!invitationList && (
          <section>
            <ControlledInputSelect
              fieldRegisterName='invitationId'
              options={invitationList.map((i: Invitation) => ({ ...i, name: invitationName(i) }))}
              rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
              label={t('invitations:selectInvitationId')}
            />
          </section>
        )}
        {offer && (
          <ControlledInputSelect
            fieldRegisterName='channelId'
            rules={{ required: { message: t(REQUIRED_TEXT_ERROR), value: true } }}
            options={channelList}
            label={t('invitations:processModalChannelSelectorTitle')}
          />
        )}
        {eventId && invitationId && channelId && (
          <>
            <Divider className='my-0' />
            <ControlledMultiOfferSelector formName='type' typeOptions={typeOptions} type={invitationType} />
            {invitationType === SINGLE_INVITE && (
              <InputCounter
                formCounterName={FIELD_QUANTITY}
                minQuantity={invitationItemsPerTransaction?.min ?? 0}
                maxQuantity={invitationItemsPerTransaction?.max ?? 0}
                title={t('invitations:totalGuestCounterText')}
                error={t('errors:minQuantityFieldError', { number: invitationItemsPerTransaction?.min })}
              />
            )}
          </>
        )}
      </div>
      <ModalStickyFooter
        primaryButtonText={t('offers:continueButton')}
        onPrimaryButtonClick={onContinue}
        isSubmitDisabled={!isValid || invitationType === '' || quantity === 0}
      />
    </>
  );
};
